import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useWeb3React } from "@web3-react/core";
import { parseUnits } from "@ethersproject/units";
import { BigNumber } from "ethers";

import colors from "shared/lib/designSystem/colors";
import {
  VaultAddressMap,
  VaultMaxDeposit,
  VaultOptions,
} from "shared/lib/constants/constants";
import { ACTIONS } from "../Modal/types";
import useVaultActionForm from "../../../../hooks/useVaultActionForm";
import { SecondaryText, Title } from "shared/lib/designSystem";
import useTokenAllowance from "shared/lib/hooks/useTokenAllowance";
import useV2VaultData from "shared/lib/hooks/useV2VaultData";
import { ERC20Token } from "shared/lib/models/eth";
import { isETHVault, isVaultFull } from "shared/lib/utils/vault";
import { formatBigNumber, isPracticallyZero } from "shared/lib/utils/math";
import VaultApprovalForm from "../common/VaultApprovalForm";
import ButtonArrow from "shared/lib/components/Common/ButtonArrow";
import theme from "shared/lib/designSystem/theme";
import SwapBTCDropdown from "../common/SwapBTCDropdown";
import VaultBasicAmountForm from "../common/VaultBasicAmountForm";
import { getAssetDisplay } from "shared/lib/utils/asset";
import { VaultValidationErrors } from "../types";
import VaultV2WithdrawForm from "./VaultV2WithdrawForm";

const FormTabContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-evenly;
  position: relative;

  &:before {
    display: block;
    position: absolute;
    height: 100%;
    width: 100%;
    content: " ";
    background-color: ${colors.background};
    z-index: -1;
  }
`;

const FormTab = styled.div<{ active: boolean }>`
  width: 100%;
  padding: 24px 0;
  background-color: ${(props) =>
    props.active ? "rgb(28, 26, 25,0.95)" : "rgba(255,255,255,0.04)"};
  cursor: pointer;

  &:after {
    background-color: white;
  }

  &:first-child {
    border-top-left-radius: 8px;
  }

  &:last-child {
    border-top-right-radius: 8px;
  }
`;

const FormTabTitle = styled(Title)<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 24px;
  text-transform: uppercase;
  color: ${(props) => (props.active ? "#f3f3f3" : "rgba(255, 255, 255, 0.64)")};
`;

const FormInfoText = styled(Title)`
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  color: ${(props) => (props.color ? props.color : colors.text)};
`;

const SwapTriggerContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const SwapTriggerButton = styled.div`
  margin-top: 24px;
  border: ${theme.border.width} ${theme.border.style} ${colors.border};
  border-radius: 100px;
  padding: 8px 16px;
  width: 300px;
  display: flex;
  align-items: center;
`;

const SwapTriggerButtonText = styled(SecondaryText)`
  flex: 1;
`;

interface VaultV2DepositWithdrawFormProps {
  vaultOption: VaultOptions;
  onFormSubmit: () => void;
}

const VaultV2DepositWithdrawForm: React.FC<VaultV2DepositWithdrawFormProps> = ({
  vaultOption,
  onFormSubmit,
}) => {
  /**
   * Primary hooks
   */
  const { handleActionTypeChange, vaultActionForm } =
    useVaultActionForm(vaultOption);
  const {
    data: {
      asset,
      cap,
      decimals,
      pricePerShare,
      depositBalanceInAsset,
      lockedBalanceInAsset,
      totalBalance,
      userAssetBalance,
      withdrawals,
    },
    loading,
  } = useV2VaultData(vaultOption, { poll: true });
  const vaultBalanceInAsset = depositBalanceInAsset.add(lockedBalanceInAsset);
  const { active } = useWeb3React();

  const vaultMaxDepositAmount = VaultMaxDeposit[vaultOption];
  const isInputNonZero = parseFloat(vaultActionForm.inputAmount) > 0;

  /**
   * Side hooks
   */
  const tokenAllowance = useTokenAllowance(
    isETHVault(vaultOption) ? undefined : (asset.toLowerCase() as ERC20Token),
    VaultAddressMap[vaultOption].v2
  );

  /**
   * Check if approval needed
   */
  const showTokenApproval = useMemo(() => {
    if (vaultActionForm.actionType === ACTIONS.deposit) {
      return (
        !isETHVault(vaultOption) &&
        tokenAllowance &&
        isPracticallyZero(tokenAllowance, decimals)
      );
    }

    return false;
  }, [decimals, tokenAllowance, vaultActionForm.actionType, vaultOption]);
  const [swapContainerOpen, setSwapContainerOpen] = useState(false);

  /**
   * Default action to deposit
   */
  useEffect(() => {
    handleActionTypeChange(ACTIONS.deposit, "v2");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const error = useMemo((): VaultValidationErrors | undefined => {
    switch (vaultActionForm.actionType) {
      case ACTIONS.deposit:
        /** Vault full */
        if (isVaultFull(totalBalance, cap, decimals)) {
          return "vaultFull";
        }

        /** Max Deposited */
        if (vaultMaxDepositAmount.lt(vaultBalanceInAsset)) {
          return "maxDeposited";
        }
        break;
    }

    try {
      /** Check block with input requirement */
      if (isInputNonZero && !loading && active) {
        const amountBigNumber = parseUnits(
          vaultActionForm.inputAmount,
          decimals
        );

        switch (vaultActionForm.actionType) {
          case ACTIONS.deposit:
            if (amountBigNumber.gt(userAssetBalance)) {
              return "insufficientBalance";
            }

            if (
              amountBigNumber.gt(vaultMaxDepositAmount.sub(vaultBalanceInAsset))
            ) {
              return "maxExceeded";
            }

            if (amountBigNumber.gt(cap.sub(totalBalance))) {
              return "capacityOverflow";
            }

            break;

          case ACTIONS.withdraw:
            switch (vaultActionForm.withdrawOption) {
              case "instant":
                if (amountBigNumber.gt(depositBalanceInAsset)) {
                  return "withdrawLimitExceeded";
                }

                break;
              case "standard":
                if (amountBigNumber.gt(lockedBalanceInAsset)) {
                  return "withdrawLimitExceeded";
                }

                break;
            }
        }
      }
    } catch (err) {
      // Assume no error because empty input unable to parse
    }

    return undefined;
  }, [
    active,
    cap,
    decimals,
    depositBalanceInAsset,
    isInputNonZero,
    loading,
    lockedBalanceInAsset,
    totalBalance,
    userAssetBalance,
    vaultActionForm.actionType,
    vaultActionForm.inputAmount,
    vaultActionForm.withdrawOption,
    vaultBalanceInAsset,
    vaultMaxDepositAmount,
  ]);

  const formContent = useMemo(() => {
    /**
     * Approval before deposit
     */
    if (showTokenApproval) {
      return <VaultApprovalForm vaultOption={vaultOption} version="v2" />;
    }

    switch (vaultActionForm.actionType) {
      case ACTIONS.deposit:
        return (
          <VaultBasicAmountForm
            vaultOption={vaultOption}
            error={error}
            onFormSubmit={onFormSubmit}
            actionButtonText="Preview Deposit"
          />
        );
      case ACTIONS.withdraw:
        return (
          <VaultV2WithdrawForm
            vaultOption={vaultOption}
            error={error}
            onFormSubmit={onFormSubmit}
            depositBalanceInAsset={depositBalanceInAsset}
            lockedBalanceInAsset={lockedBalanceInAsset}
            initiatedWithdrawAmount={withdrawals.shares
              .mul(pricePerShare)
              .div(BigNumber.from(10).pow(decimals))}
          />
        );
    }
  }, [
    decimals,
    depositBalanceInAsset,
    error,
    lockedBalanceInAsset,
    onFormSubmit,
    pricePerShare,
    showTokenApproval,
    vaultActionForm.actionType,
    vaultOption,
    withdrawals.shares,
  ]);

  const formInfoText = useMemo(() => {
    switch (error) {
      case "vaultFull":
        return (
          <FormInfoText color={colors.red}>
            The Vault is currently full
          </FormInfoText>
        );
      case "maxDeposited":
        return (
          <FormInfoText color={colors.red}>
            This vault has a max deposit of{" "}
            {formatBigNumber(vaultMaxDepositAmount, decimals)} $
            {getAssetDisplay(asset)} per depositor
          </FormInfoText>
        );
    }

    switch (vaultActionForm.actionType) {
      case ACTIONS.deposit:
        if (!active || loading) {
          return <FormInfoText>---</FormInfoText>;
        }

        return (
          <FormInfoText
            color={error === "insufficientBalance" ? colors.red : undefined}
          >
            Wallet Balance: {formatBigNumber(userAssetBalance, decimals)}{" "}
            {getAssetDisplay(asset)}
          </FormInfoText>
        );
    }
  }, [
    active,
    asset,
    decimals,
    error,
    loading,
    userAssetBalance,
    vaultActionForm.actionType,
    vaultMaxDepositAmount,
  ]);

  const swapContainerTrigger = useMemo(() => {
    switch (asset) {
      case "WBTC":
        return (
          <SwapTriggerContainer>
            <SwapTriggerButton
              role="button"
              onClick={() => setSwapContainerOpen((open) => !open)}
            >
              <SwapTriggerButtonText>
                Swap your BTC or renBTC for wBTC
              </SwapTriggerButtonText>
              <ButtonArrow
                isOpen={swapContainerOpen}
                color={colors.primaryText}
              />
            </SwapTriggerButton>
          </SwapTriggerContainer>
        );

      default:
        return <></>;
    }
  }, [asset, swapContainerOpen]);

  const swapContainer = useMemo(() => {
    switch (asset) {
      case "WBTC":
        return <SwapBTCDropdown open={swapContainerOpen} />;
      default:
        return <></>;
    }
  }, [asset, swapContainerOpen]);

  return (
    <>
      <FormTabContainer>
        <FormTab
          active={vaultActionForm.actionType === ACTIONS.deposit}
          onClick={() => handleActionTypeChange(ACTIONS.deposit, "v2")}
        >
          <FormTabTitle active={vaultActionForm.actionType === ACTIONS.deposit}>
            Deposit
          </FormTabTitle>
        </FormTab>
        <FormTab
          active={vaultActionForm.actionType === ACTIONS.withdraw}
          onClick={() => handleActionTypeChange(ACTIONS.withdraw, "v2")}
        >
          <FormTabTitle
            active={vaultActionForm.actionType === ACTIONS.withdraw}
          >
            Withdraw
          </FormTabTitle>
        </FormTab>
      </FormTabContainer>

      <div className="d-flex flex-column p-4 w-100">
        {formContent}
        {formInfoText}
        {swapContainerTrigger}
      </div>

      {swapContainer}
    </>
  );
};

export default VaultV2DepositWithdrawForm;
